import { useCallback, useMemo } from 'react';

import { CommandBarPriority } from '#components/command-bar/command-bar-types.js';
import { AddCommandBarProvider } from '#components/command-bar/components/add-command-bar-provider.js';
import { useAbilityContext } from '#state/contexts/ability-context.js';
import { cadstromUidResultHandlers } from './cadstrom-uid-handlers.js';
import { CADSTROM_UID_PROVIDER_ID } from './cadstrom-uid-types.js';
import { resolveCadstromUidType, supportedCadstromUidTypes } from './cadstrom-uid-utils.js';

import type { CadstromUidProviderProps } from './cadstrom-uid-types.js';

/**
 * Registers the Cadstrom UID command-bar provider globally.
 */
export function RegisterCadstromUidProvider() {
	const ability = useAbilityContext();
	const canViewPackages = ability.can('view', 'package');
	const supportedTypes = useMemo(() => supportedCadstromUidTypes(canViewPackages), [canViewPackages]);

	const getRelevance = useCallback(
		(search: string) => {
			return resolveCadstromUidType(search.trim(), supportedTypes) !== null ?
					CommandBarPriority.VeryHigh
				:	CommandBarPriority.VeryLow;
		},
		[supportedTypes],
	);

	const provider = useMemo(
		() => ({
			id: CADSTROM_UID_PROVIDER_ID,
			component: CadstromUidResults,
			getRelevance,
			props: { supportedTypes },
		}),
		[getRelevance, supportedTypes],
	);

	return <AddCommandBarProvider<CadstromUidProviderProps> provider={provider} />;
}

function CadstromUidResults({ search, onClose, supportedTypes }: CadstromUidProviderProps) {
	const uid = search.trim();

	const objectType = useMemo(() => resolveCadstromUidType(uid, supportedTypes), [supportedTypes, uid]);

	if (objectType === null) {
		return null;
	}

	const ResultComponent = cadstromUidResultHandlers[objectType];
	if (!ResultComponent) {
		return null;
	}

	return <ResultComponent uid={uid} onClose={onClose} />;
}
